import React, { useMemo, useState } from 'react'
import ReactDataTable from 'react-data-table-component'
import {
    IconEdit,
    IconTrash,
    IconCheck,
    IconX
} from '@tabler/icons-react'
import { useAuth } from '../../contexts/AuthContext'
import { useLeaveTypes, useCreateLeaveType, useUpdateLeaveType, useDeleteLeaveType } from '../../hooks/useLeaveTypes'
import toast from 'react-hot-toast';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import ActionDropdown from '../../components/common/ActionDropdown';
import Breadcrumb from '../../components/common/Breadcrumb';

export default function LeaveTypes() {
    const { user } = useAuth()
    const orgId = user?.orgId
    const { data: leaveTypes = [], isLoading } = useLeaveTypes(orgId)
    const createLeaveTypeMutation = useCreateLeaveType()
    const updateLeaveTypeMutation = useUpdateLeaveType()
    const deleteLeaveTypeMutation = useDeleteLeaveType()

    const [addModal, setAddModal] = useState(false)
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        max_days_per_year: '',
        requires_approval: true
    })

    // Edit Modal State
    const [editModal, setEditModal] = useState(false)
    const [editItem, setEditItem] = useState(null)
    const [editFormData, setEditFormData] = useState({
        code: '',
        name: '',
        max_days_per_year: '',
        requires_approval: true
    })

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const openAddModal = () => {
        setAddModal(!addModal)
        // Reset form
        if (!addModal) {
            setFormData({
                code: '',
                name: '',
                max_days_per_year: '',
                requires_approval: true
            })
        }
    }

    const openEditModal = (row) => {
        setEditItem(row)
        setEditFormData({
            code: row.code || '',
            name: row.name || '',
            max_days_per_year: row.max_days_per_year || '',
            requires_approval: row.requires_approval ?? true
        })
        setEditModal(true)
    }

    const handleEditSubmit = (e) => {
        e.preventDefault()
        if (!editFormData.name || !editFormData.code || !editFormData.max_days_per_year) {
            toast.error('Please fill in all required fields')
            return
        }
        updateLeaveTypeMutation.mutate(
            { id: editItem.id, data: editFormData, orgId },
            {
                onSuccess: () => {
                    toast.success(`Leave type "${editFormData.name}" updated successfully`)
                    setEditModal(false)
                    setEditItem(null)
                },
                onError: (err) => toast.error(err.message || 'Failed to update leave type')
            }
        )
    }

    const handleDelete = (row) => {
        setItemToDelete(row);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteLeaveTypeMutation.mutateAsync({ id: itemToDelete.id, orgId });
            toast.success(`Leave type "${itemToDelete.name}" deleted successfully`);
            setDeleteModalOpen(false);
            setItemToDelete(null);
        } catch (error) {
            console.error('Error deleting leave type:', error);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!formData.name || !formData.code || !formData.max_days_per_year) {
            toast.error('Please fill in all required fields')
            return
        }

        createLeaveTypeMutation.mutate(
            { data: formData, orgId },
            {
                onSuccess: (result) => {
                    const name = result.leave_type?.name || result.name || formData.name;
                    toast.success(`Leave type "${name}" created successfully`)
                    openAddModal()
                },
                onError: (err) => toast.error(err.message || 'Failed to create leave type')
            }
        )
    }

    const columns = [
        {
            name: 'Code',
            selector: row => row.code,
            sortable: true,
            width: '150px'
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Max Days/Year',
            selector: row => row.max_days_per_year,
            sortable: true,
            center: true
        },
        {
            name: 'Requires Approval',
            selector: row => row.requires_approval,
            cell: row => row.requires_approval ? <IconCheck className="text-success" size={20} /> : <IconX className="text-danger" size={20} />,
            center: true
        },
        {
            name: 'Action',
            cell: row => (
                <div className="flex justify-center">
                    <ActionDropdown
                        menuItems={[
                            {
                                label: 'Edit Leave Type',
                                icon: IconEdit,
                                onClick: () => openEditModal(row),
                                description: "Update code, name or max days"
                            },
                            {
                                label: 'Delete Leave Type',
                                icon: IconTrash,
                                danger: true,
                                onClick: () => handleDelete(row),
                                description: "Permanently remove this type"
                            }
                        ]}
                    />
                </div>
            ),
            width: "120px",
            center: true
        }
    ]

    const [filterText, setFilterText] = useState('')
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

    const filteredItems = leaveTypes.filter(item =>
        item.name?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.code?.toLowerCase().includes(filterText.toLowerCase())
    )

    const subHeaderComponentMemo = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText('');
            }
        };

        return (
            <div className='flex gap-5 mb-10 w-full justify-end'>
                <div className='relative w-full max-w-[221px]'>
                    <input
                        type='text'
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        placeholder='Search types...'
                        className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none'
                    />
                </div>
                <button onClick={handleClear} className="btn btn-danger">
                    Clear
                </button>
            </div>
        );
    }, [filterText, resetPaginationToggle]);

    if (isLoading) return <div>Loading...</div>

    return (
        <div className='flex flex-col gap-20'>
            <Breadcrumb title="Leave Types" path="Leave / Leave Types" />
            <div className='bg-bg-dark rounded-lg shadow-light md:p-20 p-15'>
            <div className='flex items-center justify-between gap-15 mb-30'>
                <div>
                    <p className='text-[16px]/[19.2px] font-medium'>
                        Leave Types
                    </p>
                    <p className='text-xs text-text-secondary mt-1'>
                        Manage organization leave types
                    </p>
                </div>

                <button onClick={openAddModal} className='btn btn-primary'>
                    Add New Type
                </button>
            </div>
            <div className='react-data-table'>
                <ReactDataTable
                    columns={columns}
                    data={filteredItems}
                    pagination
                    paginationResetDefaultPage={resetPaginationToggle}
                    subHeader
                    subHeaderComponent={subHeaderComponentMemo}
                />
            </div>

            {addModal && (
                <>
                    <div className='fixed p-15 w-full max-w-[500px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]'>
                        <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                            <h5 className="text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border">
                                Add Leave Type
                            </h5>
                            <div className='my-10 md:px-10 px-[7px] overflow-auto'>
                                <form onSubmit={handleSubmit}>
                                    <div className='grid grid-cols-12 gap-20'>
                                        <div className='form-group col-span-12'>
                                            <label className='text-sm mb-2 block'>Code *</label>
                                            <input
                                                type='text'
                                                className='w-full'
                                                value={formData.code}
                                                onChange={e => setFormData({ ...formData, code: e.target.value })}
                                                placeholder='e.g., ANN'
                                            />
                                        </div>
                                        <div className='form-group col-span-12'>
                                            <label className='text-sm mb-2 block'>Name *</label>
                                            <input
                                                type='text'
                                                className='w-full'
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                placeholder='e.g., Annual Leave'
                                            />
                                        </div>
                                        <div className='form-group col-span-12'>
                                            <label className='text-sm mb-2 block'>Max Days Per Year *</label>
                                            <input
                                                type='number'
                                                step='0.1'
                                                className='w-full'
                                                value={formData.max_days_per_year}
                                                onChange={e => setFormData({ ...formData, max_days_per_year: e.target.value })}
                                                placeholder='e.g., 20.0'
                                            />
                                        </div>
                                        <div className='form-group col-span-12 flex items-center justify-end gap-2  w-full'>
                                            <div className='flex w-full ml-auto col-span-6 items-center gap-2 '>
                                                <input
                                                    className=''
                                                    type='checkbox'
                                                    id='requiresApproval'
                                                    checked={formData.requires_approval}
                                                    onChange={e => setFormData({ ...formData, requires_approval: e.target.checked })}
                                                />
                                                <label htmlFor='requiresApproval' className='text-sm cursor-pointer'>Requires Approval</label>

                                            </div>   </div>
                                    </div>
                                    <div className='flex gap-10 justify-end mt-20'>
                                        <button type='button' onClick={openAddModal} className='btn btn-outline-secondary'>
                                            Cancel
                                        </button>
                                        <button type='submit' className='btn btn-primary' disabled={createLeaveTypeMutation.isPending}>
                                            {createLeaveTypeMutation.isPending ? 'Creating...' : 'Create'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div onClick={openAddModal} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
                </>
            )}
            {editModal && (
                <>
                    <div className='fixed p-15 w-full max-w-[500px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]'>
                        <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                            <h5 className="text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border">
                                Edit Leave Type
                            </h5>
                            <div className='my-10 md:px-10 px-[7px] overflow-auto'>
                                <form onSubmit={handleEditSubmit}>
                                    <div className='grid grid-cols-12 gap-20'>
                                        <div className='form-group col-span-12'>
                                            <label className='text-sm mb-2 block'>Code *</label>
                                            <input
                                                type='text'
                                                className='w-full'
                                                value={editFormData.code}
                                                onChange={e => setEditFormData({ ...editFormData, code: e.target.value })}
                                                placeholder='e.g., ANN'
                                            />
                                        </div>
                                        <div className='form-group col-span-12'>
                                            <label className='text-sm mb-2 block'>Name *</label>
                                            <input
                                                type='text'
                                                className='w-full'
                                                value={editFormData.name}
                                                onChange={e => setEditFormData({ ...editFormData, name: e.target.value })}
                                                placeholder='e.g., Annual Leave'
                                            />
                                        </div>
                                        <div className='form-group col-span-12'>
                                            <label className='text-sm mb-2 block'>Max Days Per Year *</label>
                                            <input
                                                type='number'
                                                step='0.1'
                                                className='w-full'
                                                value={editFormData.max_days_per_year}
                                                onChange={e => setEditFormData({ ...editFormData, max_days_per_year: e.target.value })}
                                                placeholder='e.g., 20.0'
                                            />
                                        </div>
                                        <div className='form-group col-span-12 flex items-center justify-end gap-2 w-full'>
                                            <div className='flex w-full ml-auto col-span-6 items-center gap-2'>
                                                <input
                                                    type='checkbox'
                                                    id='editRequiresApproval'
                                                    checked={editFormData.requires_approval}
                                                    onChange={e => setEditFormData({ ...editFormData, requires_approval: e.target.checked })}
                                                />
                                                <label htmlFor='editRequiresApproval' className='text-sm cursor-pointer'>Requires Approval</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex gap-10 justify-end mt-20'>
                                        <button type='button' onClick={() => setEditModal(false)} className='btn btn-outline-secondary'>
                                            Cancel
                                        </button>
                                        <button type='submit' className='btn btn-primary' disabled={updateLeaveTypeMutation.isPending}>
                                            {updateLeaveTypeMutation.isPending ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div onClick={() => setEditModal(false)} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
                </>
            )}
            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
                onConfirm={confirmDelete}
                title="Delete Leave Type"
                message="Are you sure you want to delete this leave type? This will prevent users from selecting it for new leave requests."
                itemName={itemToDelete?.name}
                isLoading={deleteLeaveTypeMutation.isPending}
            />
            </div>
        </div>
    )
}
